import { Component } from '@theme/component';
import { mediaQueryLarge } from '@theme/utilities';

/**
 * A single silent player with breakpoint-specific sources and a shared poster.
 * Native controls stay in the markup as a no-JS fallback, but once this runs,
 * the `controls` attribute itself is removed on mobile (unless the block's
 * "Show controls on mobile" setting is on) rather than merely hidden by CSS —
 * on a real device, tapping a video with `controls` present re-triggers the
 * browser's native overlay (iOS and Android both) no matter what CSS says, so
 * only removing the attribute actually stops it (SM-734). Desktop always
 * keeps it.
 *
 * @typedef {object} ResponsiveVideoRefs
 * @property {HTMLVideoElement} [video] - Absent in the empty/poster-only state.
 * @property {HTMLTemplateElement} [desktopSources] - Desktop source candidates.
 * @property {HTMLTemplateElement} [mobileSources] - Mobile source candidates.
 * @extends {Component<ResponsiveVideoRefs>}
 */
export class ResponsiveVideo extends Component {
  #motion = matchMedia('(prefers-reduced-motion: reduce)');
  /** @type {AbortController | undefined} */
  #listeners;
  /** @type {IntersectionObserver | undefined} */
  #visibility;
  #activeSources = '';
  #autoplayAttempted = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initialise();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialise();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#visibility?.disconnect();
    this.refs.video?.pause();
    super.disconnectedCallback();
  }

  /** Wire this instance, including after a theme-editor update. */
  #initialise() {
    this.#listeners?.abort();
    this.#visibility?.disconnect();
    const { video } = this.refs;
    if (!video) return;

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    video.muted = true;
    video.defaultMuted = true;
    // The server-rendered sources are a no-JavaScript fallback. Request no
    // preload, then select the active sources before starting playback.
    this.#activeSources = '';
    this.#syncSource();
    mediaQueryLarge.addEventListener('change', this.#syncSource, { signal });
    video.addEventListener('play', () => { this.#autoplayAttempted = true; }, { signal });
    this.#motion.addEventListener('change', () => {
      if (this.#motion.matches) {
        this.#autoplayAttempted = true;
        video.pause();
      }
    }, { signal });

    if (this.dataset.autoplay !== 'true' || this.#motion.matches || this.#autoplayAttempted) return;
    this.#visibility = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.#visibility?.disconnect();
      if (this.#autoplayAttempted || this.#motion.matches) return;
      this.#autoplayAttempted = true;
      this.#play();
    });
    this.#visibility.observe(video);
  }

  /** Select sources without downloading a hidden second player. */
  #syncSource = () => {
    const { video, desktopSources, mobileSources } = this.refs;
    const selected = mediaQueryLarge.matches ? desktopSources : mobileSources;
    if (!video || !selected) return;

    const showControlsOnMobile = this.classList.contains('responsive-video--show-controls-mobile');
    if (mediaQueryLarge.matches || showControlsOnMobile) video.setAttribute('controls', '');
    else video.removeAttribute('controls');

    if (selected.dataset.poster) video.poster = selected.dataset.poster;
    else video.removeAttribute('poster');
    video.setAttribute('aria-label', selected.dataset.label ?? '');
    if (this.#activeSources === selected.innerHTML) return;

    const resume = !video.paused;
    this.#activeSources = selected.innerHTML;
    video.replaceChildren(selected.content.cloneNode(true));
    video.load();
    // A paused video stays paused when resizing. Different edits start at the
    // beginning because the mobile and desktop clips may have different cuts.
    if (resume) this.#play();
  };

  /** Autoplay rejection or an interrupted source load leaves manual controls. */
  #play() {
    this.refs.video?.play().catch(() => {});
  }
}

if (!customElements.get('responsive-video')) {
  customElements.define('responsive-video', ResponsiveVideo);
}
